import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
} from '@angular/core';
import {
  type IPractice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  toTimestamp,
  type ITimePeriod,
  type WithRef,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { ReplaySubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PractitionerTransactionsReportStore } from './practitioner-transactions-report.store';

export interface IReportRequest {
  dateRange: ITimePeriod;
  practices: WithRef<IPractice>[];
  practitionerRef?: DocumentReference<IStaffer>;
}

@Component({
  selector: 'pr-practitioner-transactions-report',
  templateUrl: './practitioner-transactions-report.component.html',
  styleUrls: ['./practitioner-transactions-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PractitionerTransactionsReportComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  request$ = new ReplaySubject<IReportRequest>(1);

  @Input()
  set request(request: IReportRequest) {
    if (request) {
      this.request$.next(request);
    }
  }

  constructor(public store: PractitionerTransactionsReportStore) {
    const query$ = this.request$.pipe(
      map(({ practices, dateRange, practitionerRef }) => {
        const practice = first(practices);
        if (!practice) {
          return;
        }

        return {
          startDate: toTimestamp(dateRange.from),
          endDate: toTimestamp(dateRange.to),
          practiceRef: practice.ref,
          practitionerRef,
          labJobTimestampField:
            practice.settings.accounting?.practitionerIncome?.labJobTimestamp,
        };
      }),
      filterUndefined()
    );

    this.store.loadTransactions(query$);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
